import type { Shape, TextAnnotation } from "../canvas-kit/drawing/types.js";
import { emptyWhiteboardSnapshot, type StickyNote, type WhiteboardSnapshot } from "./snapshot.js";

/**
 * Whiteboard templates — predefined snapshot recipes the user can apply to
 * a fresh whiteboard. Each template returns a fully-formed
 * {@link WhiteboardSnapshot} so consumption is just `setSnap(template())`.
 *
 * Templates intentionally produce concrete primitives (shapes + texts +
 * stickies) so they are first-class editable content, not a hidden layer
 * the user can't manipulate.
 */
export interface WhiteboardTemplate {
  id: string;
  label: string;
  description: string;
  build: () => WhiteboardSnapshot;
}

const INK_LIGHT = "rgba(255,255,255,0.85)";

function rect(x: number, y: number, w: number, h: number, color = INK_LIGHT): Shape {
  return {
    id: crypto.randomUUID(),
    kind: "rect",
    x,
    y,
    w,
    h,
    color,
    width: 2,
    dashed: true,
  };
}

function text(
  x: number,
  y: number,
  value: string,
  fontSize = 28,
  color = INK_LIGHT,
): TextAnnotation {
  return {
    id: crypto.randomUUID(),
    x,
    y,
    value,
    color,
    fontSize,
  };
}

function sticky(
  x: number,
  y: number,
  value: string,
  color: StickyNote["color"] = "yellow",
): StickyNote {
  return {
    id: crypto.randomUUID(),
    x,
    y,
    w: 240,
    h: 200,
    value,
    color,
    fontSize: 26,
  };
}

export const WHITEBOARD_TEMPLATES: readonly WhiteboardTemplate[] = [
  {
    id: "blank",
    label: "Leer",
    description: "Frisches Whiteboard ohne Vorgaben",
    build: () => emptyWhiteboardSnapshot(),
  },
  {
    id: "storyboard-3",
    label: "Storyboard 3 Panels",
    description: "Drei beschriftbare Panels für eine kurze Sequenz",
    build: () => {
      const base = emptyWhiteboardSnapshot();
      const w = 380;
      const h = 260;
      const gap = 60;
      const startX = -((w * 3 + gap * 2) / 2);
      const y = -h / 2;
      const shapes: Shape[] = [];
      const texts: TextAnnotation[] = [];
      for (let i = 0; i < 3; i += 1) {
        const x = startX + i * (w + gap);
        shapes.push(rect(x, y, w, h));
        texts.push(text(x + 12, y + 12, `Panel ${i + 1}`, 22));
      }
      return { ...base, shapes, texts };
    },
  },
  {
    id: "grid-3x3",
    label: "3×3 Raster",
    description: "Klassisches Hilfsraster für Layouts oder Taktik-Zonen",
    build: () => {
      const base = emptyWhiteboardSnapshot();
      const cell = 220;
      const start = -((cell * 3) / 2);
      const shapes: Shape[] = [];
      for (let r = 0; r < 3; r += 1) {
        for (let c = 0; c < 3; c += 1) {
          shapes.push(rect(start + c * cell, start + r * cell, cell, cell));
        }
      }
      return { ...base, shapes };
    },
  },
  {
    id: "mindmap",
    label: "Mindmap-Start",
    description: "Zentraler Hub + vier Äste mit Sticky-Notizen",
    build: () => {
      const base = emptyWhiteboardSnapshot();
      const cx = 0;
      const cy = 0;
      const shapes: Shape[] = [
        {
          id: crypto.randomUUID(),
          kind: "circle",
          x: cx,
          y: cy,
          w: 90,
          h: 90,
          color: INK_LIGHT,
          width: 3,
          dashed: false,
        },
      ];
      const texts: TextAnnotation[] = [text(cx - 60, cy - 18, "Thema", 26)];
      const stickies: StickyNote[] = [];
      const radius = 320;
      const labels: Array<[StickyNote["color"], string]> = [
        ["yellow", "Idee 1"],
        ["blue", "Idee 2"],
        ["pink", "Idee 3"],
        ["green", "Idee 4"],
      ];
      labels.forEach(([color, label], i) => {
        const angle = (i * Math.PI) / 2 - Math.PI / 4;
        const x = cx + Math.cos(angle) * radius - 120;
        const y = cy + Math.sin(angle) * radius - 100;
        stickies.push(sticky(x, y, label, color));
        shapes.push({
          id: crypto.randomUUID(),
          kind: "line",
          x: cx + Math.cos(angle) * 50,
          y: cy + Math.sin(angle) * 50,
          w: Math.cos(angle) * (radius - 50),
          h: Math.sin(angle) * (radius - 50),
          color: INK_LIGHT,
          width: 2,
          dashed: false,
        });
      });
      return { ...base, shapes, texts, stickies };
    },
  },
  {
    id: "tactical-half",
    label: "Halbfeld-Skizze",
    description: "Rechteck eines Halbfelds mit Beschriftung",
    build: () => {
      const base = emptyWhiteboardSnapshot();
      const w = 820;
      const h = 540;
      return {
        ...base,
        shapes: [rect(-w / 2, -h / 2, w, h), rect(-w / 2, -h / 2, w, h / 3)],
        texts: [text(-w / 2 + 16, -h / 2 + 12, "Verteidigungsdrittel", 22)],
      };
    },
  },
] as const;
